/**
 * The booking-money tender CONTROLS (DNKADM-0402 U2) — the card-on-file chooser, the
 * secure card field and the payment-method select, mounted by the create-on-behalf
 * dialog and reused verbatim by Take payment (U5).
 *
 * They are the SAME shared components `SellCartDialog` uses, with the same rules:
 *  - {@link CardOnFileChooser} is mounted only while a card is being taken, so its list
 *    is never cached beyond the moment of the charge;
 *  - {@link CardPaymentField} is rendered only when no card on file is chosen — there
 *    is then nothing to tokenize;
 *  - the method select lists the tenant's ACTIVE payment methods and its labels are
 *    backend text, so they go through `scrubBrand` at the render boundary.
 * Nothing is forked here: this file composes them and owns no money logic (that lives
 * in `reservationTender.ts`).
 */
import { useId } from 'react';
import { Alert, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { inkAlpha } from '@/theme';
import { scrubBrand } from '@/utils/whitelabel';
import CardPaymentField from '@/components/payments/CardPaymentField';
import { CardOnFileChooser } from '@/features/members/CardOnFileChooser';
import type { ReservationTender, TenderChoice } from './reservationTender';

const PLACEHOLDER = '-';

interface ReservationTenderFieldsProps {
  /** Which tender is being taken. */
  choice: TenderChoice;
  /**
   * The booking's member — whose cards on file are offered. `null` when the read carries
   * no linked contact: there is then no card list to fetch, so the chooser is simply not
   * rendered and a card is entered at the desk instead. It is never a dead end — the
   * shared chooser already treats "no cards on file" the same way (DNKADM-0253).
   */
  contactId: string | null;
  /** The signed-in operator's tenant, for the secure card field. */
  tenantSlug: string;
  /** The booking's v2 location, for the secure card field's payment connection. */
  locationRef: string;
  tender: ReservationTender;
  disabled?: boolean;
}

export function ReservationTenderFields({
  choice,
  contactId,
  tenantSlug,
  locationRef,
  tender,
  disabled = false,
}: ReservationTenderFieldsProps) {
  const theme = useTheme();
  const methodId = `${useId()}-tender-method`;

  if (choice === 'card') {
    if (tenantSlug.trim().length === 0 || locationRef.trim().length === 0) {
      return (
        <Alert severity="warning">
          {tenantSlug.trim().length === 0
            ? 'Tenant payment context is unavailable. Sign in again before charging a card.'
            : 'Select a location before charging the card.'}
        </Alert>
      );
    }
    return (
      <Stack sx={{ gap: 1 }}>
        {contactId !== null && (
          <CardOnFileChooser
            contactId={contactId}
            value={tender.cardOnFileId}
            onChange={(next) => {
              tender.setCardOnFileId(next);
              // Changing the credential drops any card entry in progress with it.
              if (next !== null) tender.setTokenize(null);
            }}
            disabled={disabled}
          />
        )}
        {tender.cardOnFileId === null && (
          <CardPaymentField
            tenantSlug={tenantSlug}
            locationRef={locationRef}
            disabled={disabled}
            onReady={(tokenize) => tender.setTokenize(tokenize)}
            onError={() => tender.setTokenize(null)}
          />
        )}
      </Stack>
    );
  }

  return (
    <TextField
      id={methodId}
      label="Payment method"
      select
      required
      fullWidth
      size="small"
      value={tender.selectedMethod === null ? '' : tender.paymentMethodId}
      onChange={(event) => tender.setPaymentMethodId(event.target.value)}
      disabled={disabled || tender.methods.status !== 'ready'}
      error={tender.methods.status === 'error'}
      helperText={
        tender.methods.status === 'error'
          ? tender.methods.message
          : 'How the money was taken at the desk.'
      }
    >
      {tender.methods.status === 'loading' && (
        <MenuItem value="" disabled>
          <Typography sx={{ fontSize: 14, color: inkAlpha(theme, 0.62) }}>
            Loading payment methods...
          </Typography>
        </MenuItem>
      )}
      {tender.methods.status === 'ready' && tender.methods.methods.length === 0 && (
        <MenuItem value="" disabled>
          No payment methods available
        </MenuItem>
      )}
      {tender.methods.status === 'ready' &&
        tender.methods.methods.map((method) => (
          <MenuItem key={method.id} value={method.id}>
            {scrubBrand(method.label) ?? PLACEHOLDER}
          </MenuItem>
        ))}
    </TextField>
  );
}
